<template>
  <section class="testimonial-carousel-section">
    <p style="font-family: 'Roboto', sans-serif; font-size: 18px; color: #00a0c1; margin-bottom: 12px;"><strong>What Our Clients Say</strong></p>

    <div
      v-if="testimonials.length > 1"
      id="testimonialsCarousel"
      ref="carouselRef"
      class="carousel slide"
      data-bs-ride="carousel"
      :data-bs-interval="autoplayInterval"
    >
    
      <div class="carousel-inner">
        <div
          v-for="(testimonial, index) in testimonials"
          :key="testimonial.name"
          :class="['carousel-item', { active: index === 0 }]"
        >
          <div class="testimonial-card">
            <p class="testimonial-quote">"{{ testimonial.quote }}"</p>
            <div class="testimonial-author">
              <img class="testimonial-avatar" :src="testimonial.avatar" :alt="testimonial.name" />
              <div class="author-info">
                <div class="testimonial-name">{{ testimonial.name }}</div>
                <div class="testimonial-details">
                  <span v-if="testimonial.role">{{ testimonial.role }}</span>
                  <span v-if="testimonial.role && testimonial.company"> - </span>
                  <span v-if="testimonial.company">{{ testimonial.company }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="carousel-indicators">
        <button
          v-for="(slide, index) in testimonials"
          :key="index"
          type="button"
          data-bs-target="#testimonialsCarousel"
          :data-bs-slide-to="index"
          :class="{ active: index === 0 }"
          :aria-label="'Slide ' + (index + 1)"
        ></button>
      </div>
    </div>

    <div v-else-if="testimonials.length === 1" class="testimonials-grid">
      <div class="testimonial-card">
        <p class="testimonial-quote">"{{ testimonials[0].quote }}"</p>
        <div class="testimonial-author">
          <img class="testimonial-avatar" :src="testimonials[0].avatar" :alt="testimonials[0].name" />
          <div class="author-info">
            <div class="testimonial-name">{{ testimonials[0].name }}</div>
            <div class="testimonial-details">
              <span v-if="testimonials[0].role">{{ testimonials[0].role }}</span>
              <span v-if="testimonials[0].role && testimonials[0].company"> - </span>
              <span v-if="testimonials[0].company">{{ testimonials[0].company }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script>
import { toRefs, ref, onMounted, watch, nextTick } from 'vue';
import { Carousel } from 'bootstrap';

export default {
  name: 'TestimonialsCarousel',
  props: {
    testimonialsData: {
      type: Array,
      required: true,
    },
    autoplayInterval: {
      type: Number,
      default: 5000,
    },
  },
  setup(props) {
    const { testimonialsData: testimonials } = toRefs(props);
    const carouselRef = ref(null);
    let carouselInstance = null;

    const initCarousel = () => {
      if (carouselRef.value && testimonials.value.length > 1) {
        if (!carouselInstance) {
          carouselInstance = new Carousel(carouselRef.value, {
            interval: props.autoplayInterval,
            ride: 'carousel'
          });
        } else {
          // If already initialized and testimonials change, maybe we just need to restart it
          carouselInstance.cycle();
        }
      }
    };

    onMounted(() => {
      initCarousel();
    });

    watch(testimonials, async () => {
      await nextTick();
      initCarousel();
    }, { deep: true });

    return {
      testimonials,
      carouselRef,
    };
  },
};
</script>

<style scoped>
/* Define CSS variables for a consistent and easily customizable theme */
.testimonial-carousel-section {
  --brand-color: #00a0c1;
  --text-primary: #334e68;
  --text-secondary: #5a728a;
  --card-bg: #ffffff;
  --section-bg: #f8f9fa;
  --border-color: #dee2e6;
}

.testimonial-carousel-section {
  background-color: var(--section-bg);
  padding: 50px 20px;
}

.section-title {
  text-align: center;
  font-size: 2em;
  font-weight: 600;
  color: var(--brand-color);
  margin-bottom: 40px;
}

.carousel-inner {
  padding: 0 40px; /* Prevent controls from overlapping content */
}

.testimonial-card {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-left: 5px solid var(--brand-color);
  border-radius: 8px;
  padding: 30px 35px;
  margin: 0 auto 20px auto;
  max-width: 800px;
  text-align: left;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.testimonial-quote {
  font-style: italic;
  color: var(--text-secondary);
  font-size: 1.1em;
  line-height: 1.6;
  margin: 0;
  padding-bottom: 20px;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 15px;
}

.testimonial-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color);
}

.author-info {
  line-height: 1.4;
}

.testimonial-name {
  font-weight: bold;
  color: var(--text-primary);
}

/* Updated style for role and company */
.testimonial-details {
  color: var(--text-secondary);
  font-size: 0.9em;
}

.testimonials-grid {
  display: flex;
  justify-content: center;
}

/* Carousel Indicator Styling */
.carousel-indicators {
  position: static;
  margin-top: 15px;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: #adb5bd;
  margin: 0 5px;
  opacity: 0.7;
}

.carousel-indicators button.active {
  background-color: var(--brand-color);
  opacity: 1;
}
</style>